import type { TablePayload } from './types';
import { workspaceErrorCopy } from './workspace-errors';

const MAX_PREVIEW_ROWS = 1000;
const MAX_PREVIEW_CELLS = 20_000;
const MAX_PREVIEW_CHARACTERS = 1024 * 1024;
const MAX_FIELD_CHARACTERS = 64 * 1024;
const MAX_COLUMNS = 200;

function isDisplayNumber(value: string | undefined): boolean {
  if (value === undefined || !/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)) return false;
  const number = Number(value);
  return (
    Number.isFinite(number) &&
    (!Number.isInteger(number) || Number.isSafeInteger(number)) &&
    String(number) === value
  );
}

/** Bounded display parser; full-catalog calculations stay with structured tools. */
export function parseFileCsv(text: string, locale?: string): TablePayload & { truncated?: true } {
  const copy = workspaceErrorCopy(locale);
  const first = text
    .replace(/^\uFEFF/, '')
    .slice(0, MAX_PREVIEW_CHARACTERS)
    .split(/\r?\n/, 1)[0]
    .replace(/"(?:[^"]|"")*"/g, '');
  const delimiter = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [],
    field = '',
    quoted = false;
  const content = text.replace(/^\uFEFF/, '');
  let index = 0;
  function pushField() {
    if (row.length >= MAX_COLUMNS) throw new Error(copy.csvColumns);
    row.push(field);
    field = '';
  }
  for (; index < Math.min(content.length, MAX_PREVIEW_CHARACTERS); index++) {
    const character = content[index];
    if (character === '"') {
      if (quoted && content[index + 1] === '"') {
        field += '"';
        index++;
      } else if (quoted || field.length === 0) quoted = !quoted;
      else field += character;
    } else if (character === delimiter && !quoted) {
      pushField();
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && content[index + 1] === '\n') index++;
      pushField();
      if (row.some((value) => value.length)) rows.push(row);
      row = [];
      // Include the header, then stop before allocating the rest of the file.
      if (rows.length > MAX_PREVIEW_ROWS) {
        index++;
        break;
      }
    } else field += character;
    if (field.length > MAX_FIELD_CHARACTERS) throw new Error(copy.csvField);
  }
  const truncated = index < content.length;
  if (!truncated && quoted) throw new Error(copy.csvQuote);
  // A character-budget cutoff may fall inside a quoted field. Omit that
  // incomplete row; never pretend the preview validates the unparsed tail.
  if (!truncated) {
    pushField();
    if (row.some((value) => value.length)) rows.push(row);
  }
  if (!rows.length) return { columns: [], rows: [], ...(truncated ? { truncated: true } : {}) };
  const width = rows.reduce((max, values) => Math.max(max, values.length), 0);
  const header = rows.shift()!;
  const previewRows = rows.slice(
    0,
    Math.min(MAX_PREVIEW_ROWS, Math.floor(MAX_PREVIEW_CELLS / width)),
  );
  const columns = Array.from({ length: width }, (_, index) => ({
    key: `column_${index}`,
    label: header[index] || `Column ${index + 1}`,
  }));
  // Convert only preview columns whose values survive display and export unchanged.
  // Unsafe integers, precision-bearing decimals and formatted codes stay strings.
  const numeric = columns.map(
    (_, index) =>
      previewRows.length > 0 && previewRows.every((values) => isDisplayNumber(values[index])),
  );
  return {
    columns,
    ...(truncated || previewRows.length < rows.length ? { truncated: true } : {}),
    rows: previewRows.map((values) =>
      Object.fromEntries(
        columns.map((column, index) => [
          column.key,
          numeric[index] ? Number(values[index]) : (values[index] ?? ''),
        ]),
      ),
    ),
  };
}
